/**
 * What Proctor and Wright are doing on the pull request, in one line, for the
 * ticket sheet's Review stage.
 *
 *   lined up   "Proctor is lined up to review PR #3817"
 *   reviewing  "Proctor is reviewing PR #3817 at cda6309 · started 8:28 PM · attempt 4"
 *   fixing     "Wright is fixing 3 findings · round 2"
 *
 *   held       "Waiting for a person to publish Proctor's review" · Open the review
 *
 * and once a review has posted and nothing is moving, what happens next, by
 * precedence: a person took the line over; the review left decisions only a
 * person can make (listed); else what it waits on (`workStage.reviewWaitOf`:
 * a person's ask, the next push, the merge). Rounds are not
 * counted against a cap any more: they draw from the ticket's one budget, and
 * a ticket out of budget asks on its own gate. A review still running is said as running: its findings are not
 * findings until it posts, so nothing here reads them off it.
 */
import { Link } from "react-router-dom";
import type { ReviewFinding, WorkItemReviewThread } from "@yagni/shared";
import type { PullRequestReview } from "./usePullRequestReview.js";
import {
  decisionFindingsLine,
  proctorQueuedLine,
  proctorReviewingLine,
  REVIEW_HELD_FOR_PERSON,
  REVIEW_HELD_OPEN,
  REVIEW_NEXT_RELEASED,
  wrightFixingAsksLine,
  wrightFixingLine,
} from "./workCopy.js";
import { reviewWaitOf, reviewWaitWords, type ReviewWaitFacts } from "./workStage.js";

export interface ProctorStatusLine {
  /** Which line it is, for the sheet and the tests. */
  kind: "queued" | "reviewing" | "fixing" | "held" | "released" | "decisions" | "waiting";
  text: string;
  /** The decision findings still open, for `decisions`. */
  decisions: Array<{ key: string; body: string; where: string | null }>;
  /** Where the line's own page is, for `held`: the review on Pull request reviews. */
  href?: string;
}

/** "8:28 PM" today, "Oct 6" before; null when there is no clock to print. */
export function startedClock(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  if (at.toDateString() === new Date().toDateString()) {
    return at.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  }
  return at.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function where(path: string | null, line: number | null): string | null {
  if (!path) return null;
  return line != null ? `${path}:${line}` : path;
}

/**
 * The decisions the posted review left open: the pull request's own threads
 * when the record has them (a resolved thread is settled), else the findings
 * the review delivered.
 */
function openDecisions(
  threads: readonly WorkItemReviewThread[],
  findings: readonly ReviewFinding[],
): ProctorStatusLine["decisions"] {
  if (threads.length > 0) {
    return threads
      .filter((thread) => thread.severity === "decision" && thread.isResolved !== true)
      .map((thread) => ({
        key: `t-${thread.commentId}`,
        body: thread.body,
        where: where(thread.path, thread.line),
      }));
  }
  return findings
    .filter((finding) => finding.severity === "decision")
    .map((finding) => ({
      key: `f-${finding.id}`,
      body: finding.body,
      where: where(finding.file, finding.line),
    }));
}

/** The one line, or null when there is nothing to say (no pull request read, no review yet). */
export function proctorStatusLine(
  review: Pick<
    PullRequestReview,
    "proctorStatus" | "inProgress" | "report" | "fixRounds" | "threads" | "deliveredFindings"
  > &
    Partial<Pick<PullRequestReview, "prStateId">>,
  facts: {
    prNumber: number | null | undefined;
    reviewer: string;
    builder: string;
    /** The review case's live attempt, when the list carries it. */
    attempt: number | null | undefined;
    /**
     * What else the ticket knows once nothing is moving: an ask open on it,
     * and the pull request's draft flag and GitHub state from the ticket read.
     */
    pr?: Omit<ReviewWaitFacts, "findingsOpen" | "proctorState">;
    /**
     * A one-attempt lane fix round running now, from the run's own session
     * (`sessionStage: fix`): the review record's fix rounds see only legacy
     * patch runs, so without this the line said "Needs your decision" over
     * Wright's fix (YAG-1079, ENT-1100).
     */
    laneFix?: { asks: number | null; round: number } | null;
  },
): ProctorStatusLine | null {
  const rounds = review.fixRounds;
  if (facts.laneFix) {
    return {
      kind: "fixing",
      text: wrightFixingAsksLine(facts.builder, facts.laneFix.asks, facts.laneFix.round),
      decisions: [],
    };
  }
  if (rounds?.running) {
    return {
      kind: "fixing",
      text: wrightFixingLine(facts.builder, rounds.runningFindings, Math.max(1, rounds.rounds)),
      decisions: [],
    };
  }
  if (review.proctorStatus === "reviewing") {
    return {
      kind: "reviewing",
      text: proctorReviewingLine({
        reviewer: facts.reviewer,
        prNumber: facts.prNumber,
        headSha: review.inProgress?.headSha ?? null,
        startedClock: startedClock(review.inProgress?.startedAt),
        attempt: facts.attempt,
      }),
      decisions: [],
    };
  }
  if (review.proctorStatus === "queued") {
    return {
      kind: "queued",
      text: proctorQueuedLine(facts.reviewer, facts.prNumber),
      decisions: [],
    };
  }
  // Proctor's review is done and held for a person to publish: the pull
  // request waits on that, not on anything moving here.
  if (review.proctorStatus === "held_for_you") {
    return {
      kind: "held",
      text: REVIEW_HELD_FOR_PERSON,
      decisions: [],
      ...(review.prStateId ? { href: `/reviews/${encodeURIComponent(review.prStateId)}` } : {}),
    };
  }
  if (!review.report) return null;
  if (rounds?.released) return { kind: "released", text: REVIEW_NEXT_RELEASED, decisions: [] };
  const decisions = openDecisions(review.threads, review.deliveredFindings);
  if (decisions.length > 0) {
    return { kind: "decisions", text: decisionFindingsLine(decisions.length), decisions };
  }
  // Nothing is moving: say what the pull request waits on, by the rule the
  // row and the case file's pill read. Only open findings a fix round will
  // address promise a push.
  const wait = reviewWaitOf({
    ...facts.pr,
    findingsOpen: hasFixableFindings(review.threads, review.deliveredFindings),
  });
  return { kind: "waiting", text: reviewWaitWords(wait), decisions: [] };
}

/** Severities a fix round leaves alone: a person's call, or a nit. */
const NOT_FIXED_IN_A_ROUND = new Set(["decision", "nit"]);

/**
 * Whether the posted review left findings a fix round addresses: the pull
 * request's own threads when the record has them (a resolved thread is
 * settled), else the findings the review delivered.
 */
function hasFixableFindings(
  threads: readonly WorkItemReviewThread[],
  findings: readonly ReviewFinding[],
): boolean {
  if (threads.length > 0) {
    return threads.some(
      (thread) => thread.isResolved !== true && !NOT_FIXED_IN_A_ROUND.has(thread.severity ?? ""),
    );
  }
  return findings.some((finding) => !NOT_FIXED_IN_A_ROUND.has(finding.severity ?? ""));
}

export function ProctorStatus({ line }: { line: ProctorStatusLine | null }) {
  if (!line) return null;
  return (
    <div className="blk" data-testid="proctor-status" data-kind={line.kind}>
      <p
        className="body-p"
        style={{ color: "var(--text-primary)" }}
        data-testid="proctor-status-line"
      >
        {line.text}
      </p>
      {line.href && (
        <p className="body-p" data-testid="proctor-status-link">
          <Link to={line.href}>{REVIEW_HELD_OPEN}</Link>
        </p>
      )}
      {line.decisions.length > 0 && (
        <ul className="body-p" data-testid="proctor-status-decisions">
          {line.decisions.map((decision) => (
            <li key={decision.key}>
              {decision.where && <code>{decision.where}</code>}
              {decision.where ? " · " : ""}
              {decision.body}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
